/**
 * Global stuff and functions
 *
 * @creator Stefano
 * @date 26-6-2018
 */

export const focusableSelector =
    'a[href]:not([disabled]), button:not([disabled]):not([role="presentation"]):not(.owl-dot), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), *[tabindex]:not([tabindex="-1"]';

export function getSafeRedirectUrl(url) {
    if (typeof url !== "string") {
        return null;
    }

    const trimmed = url.trim();
    if (!trimmed || trimmed.startsWith("//")) {
        return null;
    }

    try {
        const parsed = new URL(trimmed, location.origin);

        if (parsed.origin !== location.origin) {
            return null;
        }

        return parsed.href;
    } catch {
        return null;
    }
}

export default class Utils {
    /**
     * BUG: The next spread is jumping when hash is set, so we wait until animation end 06-05-2020
     */
    static setLocationHash(spread_id) {
        if (Utils.hasHashBinding() || location.hash) {
            if (!window.frontend.interface.isInTransition()) {
                if (!spread_id) {
                    spread_id = +window.frontend.getCurrentSpreadId();
                }

                location.hash = "s:" + spread_id;
            }
        }
    }

    static getLocationHash() {
        return +location.hash?.split("s:")?.[1];
    }

    static hasHashBinding() {
        return window.interface_settings?.settings?.history_bind === "hash";
    }

    /**
     * Return if object is empty
     * checks on :
     *  - Undefined
     *  - null
     *  - ""
     *  - []
     * @param obj
     * @returns {boolean}
     */
    static isEmpty(obj) {
        return (
            obj === undefined ||
            obj === false ||
            obj === null ||
            obj.toString() === "" ||
            obj === "" ||
            obj === "undefined" ||
            obj === "null"
        );
    }

    /**
     *
     * @param func
     * @param wait
     * @param context
     * @return {Function}
     */
    static simpleDebounce(func, wait, context) {
        return function () {
            var args = arguments;
            var later = function () {
                func.apply(context, args);
            };
            clearTimeout(context.timeout);
            context.timeout = setTimeout(later, wait);
        };
    }

    /**
     *
     * @param func
     * @param wait
     * @param immediate
     * @returns {Function}
     */
    static debounce(func, wait, immediate) {
        var timeout;
        return function () {
            var context = this,
                args = arguments;
            var later = function () {
                timeout = null;
                if (!immediate) func.apply(context, args);
            };
            var callNow = immediate && !timeout;
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
            if (callNow) func.apply(context, args);
        };
    }

    /**
     * Wrap a request in a Promise, replacing the previous `$.ajax(...).done(resolve).fail(reject)`.
     *
     * Mirrors jQuery's actual (verified against node_modules/jquery/dist/jquery.js,
     * `ajaxHandleResponses`) dataType handling rather than the commonly assumed one:
     * a response is parsed as JSON only when its Content-Type header contains
     * "json"; every other Content-Type (including a plain `.js` file's
     * "text/javascript") resolves with the raw response text - jQuery only
     * executes a response as a script when `dataType: "script"` is requested
     * explicitly, which none of the current callers do. Passing
     * `dataType: "text"` forces the raw-text path unconditionally, the same
     * way it made jQuery skip Content-Type sniffing (used by callers whose
     * response Content-Type claims JSON but whose body is not valid JSON).
     *
     * @param {Object} options - url, method/type, data, headers, contentType, dataType, credentials
     * @returns {Promise<any>} resolves with the parsed JSON or raw text, rejects on a network error or a non-2xx status
     */
    static AjaxPromise(options) {
        const { url, type, method, data, headers, contentType, dataType, credentials } = options;
        const httpMethod = (type || method || "GET").toUpperCase();
        const isBodyless = httpMethod === "GET" || httpMethod === "HEAD";

        let requestUrl = url;
        const fetchOptions = {
            method: httpMethod,
            credentials: credentials || "same-origin",
            headers: { ...headers },
        };

        if (data !== undefined) {
            const body = typeof data === "string" ? data : new URLSearchParams(data).toString();
            if (isBodyless) {
                requestUrl += (url.includes("?") ? "&" : "?") + body;
            } else {
                fetchOptions.body = body;
                fetchOptions.headers["Content-Type"] =
                    contentType ||
                    fetchOptions.headers["Content-Type"] ||
                    "application/x-www-form-urlencoded; charset=UTF-8";
            }
        } else if (contentType) {
            fetchOptions.headers["Content-Type"] = contentType;
        }

        return fetch(requestUrl, fetchOptions).then(async (response) => {
            const text = await response.text();

            if (!response.ok) {
                throw new Error(`AjaxPromise: ${httpMethod} ${requestUrl} failed with status ${response.status}`);
            }

            if (dataType === "text") {
                return text;
            }

            const responseContentType = response.headers.get("content-type") || "";
            return responseContentType.includes("json") ? JSON.parse(text) : text;
        });
    }

    /**
     * Replace %20 with a underscore and encodeurl
     *
     * @param {string} input
     * @returns {string}
     */
    static urlEncodeString(input) {
        //replace space(%20) with _ and replace %2f(/) with /
        return encodeURIComponent(input.toLowerCase())
            .replace(/%20/g, "_")
            .replace(/%2f/g, "/")
            .replace(/\.\.\//g, "/");
    }

    static isDownloaded() {
        return window.downloaded !== 0;
    }

    static createGetString(params) {
        let get = Object.keys(params)
            .map((k) => `${k}=${encodeURI(params[k])}`)
            .join("&");
        if (get) {
            get = `?${get}`;
        }
        return get;
    }

    static createEvent(event, params) {
        return new CustomEvent(event, params);
    }

    static trapFocus(element, preferredFocus) {
        let focusableEls;
        const selector = focusableSelector;
        if (Array.isArray(element)) {
            focusableEls = [];
            element.forEach((item) => {
                if (item && item.querySelectorAll) {
                    focusableEls = focusableEls.concat(Array.prototype.slice.call(item.querySelectorAll(selector)));
                    if (item.matches && item.matches(selector)) {
                        focusableEls.push(item);
                    }
                }
            });
        } else {
            focusableEls = Array.prototype.slice.call(element.querySelectorAll(selector));
            if (element.matches && element.matches(selector)) {
                focusableEls.push(element);
            }
        }
        const firstInSelectorsList = focusableEls[0];

        //sort by dom order
        focusableEls.sort((a, b) => {
            if (a === b) return 0;
            if (!a.compareDocumentPosition) {
                // support for IE8 and below
                return a.sourceIndex - b.sourceIndex;
            }
            if (a.compareDocumentPosition(b) & 2) {
                // b comes before a
                return 1;
            }
            return -1;
        });

        const firstFocusableEl = focusableEls[0];
        const lastFocusableEl = focusableEls[focusableEls.length - 1];

        const keydown = function (e) {
            const isTabPressed = e.key === "Tab" || e.keyCode === 9;

            if (!isTabPressed) {
                return;
            }

            if (e.shiftKey) {
                /* shift + tab */ if (document.activeElement === firstFocusableEl) {
                    lastFocusableEl.focus();
                    e.preventDefault();
                }
            } /* tab */ else {
                if (document.activeElement === lastFocusableEl) {
                    firstFocusableEl.focus();
                    e.preventDefault();
                }
            }
        };
        document.addEventListener("keydown", keydown);
        if (preferredFocus && focusableEls.includes(preferredFocus)) {
            preferredFocus.focus();
        } else if (firstInSelectorsList) {
            firstInSelectorsList.focus();
        } else if (firstFocusableEl) {
            firstFocusableEl.focus();
        }
        return keydown;
    }

    static releaseFocus(focusTrap) {
        document.removeEventListener("keydown", focusTrap);
    }
}
